import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Observable, of} from 'rxjs';
import {Action, Store} from '@ngrx/store';
import {catchError, map, mergeMap, switchMap, withLatestFrom} from 'rxjs/operators';
import {IToyotaLexusModelDetails, ServiceAppointmentState} from './service-appointment.reducer';
import {AppointmentService} from '../../shared/services/appointment.service';
import * as fromActions from './service-appointment.actions';
import * as fromSelectors from './service-appointment.selectors';
import {IAvailability, IAvailabilityOptions, IOpcodeResponse, IResponse,IOverview, factoryRecommendedServices} from '@signal/asp-data-commons';
import {toData} from '../app.adapter';
import {CatalogService} from '../../shared/services/catalog.service';
import {AvailabilityService} from '../../shared/services/availability.service';
import {VehicleService} from '../../shared/services/vehicle.service';
import { CustomerService } from '../../shared/services/customer.service';

@Injectable()
export class ServiceAppointmentEffects {

  
  loadModelYearAndModels: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadModelYearAndModels>(
      fromActions.ServiceAppointmentActionTypes.LOAD_MODEL_YEAR_AND_MODELS
    ),
    mergeMap((action: fromActions.LoadModelYearAndModels) =>
      this.vehicleService.getDcsSeriesData().pipe(
        map((vehicleModelYearList: IToyotaLexusModelDetails) => new fromActions.LoadModelYearAndModelsSuccess({vehicleModelYearList})),
        catchError(err => of(new fromActions.LoadModelYearAndModelsFail({error: err})))
      )
    )
  ));

  loadServiceRequests: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadServiceRequests>(
      fromActions.ServiceAppointmentActionTypes.LOAD_SERVICE_REQUESTS
    ),
    switchMap((action: fromActions.LoadServiceRequests) => {
        const payload = action instanceof fromActions.LoadServiceRequests ? action.payload : undefined;
        return this.catalogService.getServiceOpcodes(payload.opcodeRequest, payload.countryCode, payload.languageCode).pipe(
          switchMap((opcodeResponse: IResponse<IOpcodeResponse>) => toData(opcodeResponse, 201)),
          map((opcodeResponse: IOpcodeResponse) => new fromActions.LoadServiceRequestsSuccess({opcodeResponse})),
          catchError(err => of(new fromActions.LoadServiceRequestsFail({error: err})))
        );
      }
    )
  ));

  
  loadAvailableMileages: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadServiceRequests>(
      fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABLE_MILEAGES
    ),
    switchMap((action: fromActions.LoadAvailableMileages) => {
        return this.catalogService.getAvailableMileages().pipe(
          switchMap((opcodeResponse: IResponse<number[]>) => toData(opcodeResponse)),
          map((availableMileages: number[]) => new fromActions.LoadAvailableMileagesSuccess({availableMileages})),
          catchError(err => of(new fromActions.LoadAvailableMileagesFail({error: err})))
        );
      }
    )
  ));

  
  loadAvailabilityOptions: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadAvailabilityOptions>(
      fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABILITY_OPTIONS
    ),
    mergeMap((action: fromActions.LoadAvailabilityOptions) =>
      this.availabilityService.getAvailabilityOptions('service').pipe(
        switchMap((data: IResponse<IAvailabilityOptions>) => toData(data)),
        map((availabilityOptions: IAvailabilityOptions) => new fromActions.LoadAvailabilityOptionsSuccess({availabilityOptions})),
        catchError(err => of(new fromActions.LoadAvailabilityOptionsFail({error: err})))
      )
    )
  ));

  
  loadAvailability: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadAvailability>(
      fromActions.ServiceAppointmentActionTypes.LOAD_AVAILABILITY
    ),
    withLatestFrom(this.serviceAppointmentState.select(fromSelectors.selectServiceOpcodes), this.serviceAppointmentState.select(fromSelectors.selectRecallCodes)),
    switchMap(([action, opcodes, recallCodes]: [fromActions.LoadAvailability, string[], string[]]) =>
      this.availabilityService.getAvailability('service', action.payload, opcodes, recallCodes).pipe(
        switchMap((data: IResponse<IAvailability>) => toData(data, 201)),
        map((availability: IAvailability) => new fromActions.LoadAvailabilitySuccess({availability, loadPayload: action.payload})),
        catchError(err => of(new fromActions.LoadAvailabilityFail({error: err})))
      )
    )
  ));
  
  loadNotificationDashboardData: Observable<Action> = createEffect(() => this.actions$.pipe(
      ofType<fromActions.LoadNotificationDashboardData>(
          fromActions.ServiceAppointmentActionTypes.LOAD_NOTIFICATION_DASHBOARD_DATA
      ),
      mergeMap((action: fromActions.LoadNotificationDashboardData) =>
          this.availabilityService.getNotificationDashboardData(action.payload.department).pipe(
              switchMap((data: IResponse<IOverview[]>) => toData(data)),
              map((data: IOverview[]) => new fromActions.LoadNotificationDashboardDataSuccess({ data })),
              catchError(err => of(new fromActions.LoadNotificationDashboardDataFailure({ error: err })))
          )
      )
  ));
  
  loadStateCitiesForZip:Observable<Action>= createEffect(() =>this.actions$.pipe(
    ofType<fromActions.LoadStatesAndCities>(
      fromActions.ServiceAppointmentActionTypes.LOAD_STATES_CITIES
    ),
    switchMap((action: fromActions.LoadStatesAndCities)  => {
      const payload = action instanceof fromActions.LoadStatesAndCities ? action.payload.zipPinCode : '';
      return this.catalogService.getStateCitiesForZipcode(payload).pipe(
        switchMap((data: IResponse<any>) => toData(data,201)),
        map((data: any) => new fromActions.LoadStatesAndCitiesSuccess({data:data.zipCodeResponse})),
        catchError(err => of(new fromActions.LoadAvailabilityOptionsFail({error: err})))
      );
    }
    )
));

loadRecommendedService: Observable<Action> = createEffect(() => this.actions$.pipe(
  ofType<fromActions.LoadRecommendedService>(
    fromActions.ServiceAppointmentActionTypes.LOAD_RECOMMENDED_SERVICE
  ),
  switchMap((action: fromActions.LoadRecommendedService) => {
      const payload = action instanceof fromActions.LoadRecommendedService ? action.payload : undefined;
      return this.catalogService.getRecommendedService(payload.opcodeRequest,payload.engineDetail,payload.isLexus).pipe(
        switchMap((factoryServices: IResponse<factoryRecommendedServices[]>) => toData(factoryServices, 201)),
        map((factoryService: factoryRecommendedServices[]) => new fromActions.LoadRecommendedServiceSuccess({data:factoryService})),
        catchError(err => of(new fromActions.LoadRecommendedServiceFail({error: err})))
      );
    }
  )
));

  loadServiceAppointmentFromSignalDeepLink: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<fromActions.LoadServiceAppointmentFromSignalDeepLink>(
      fromActions.ServiceAppointmentActionTypes.LOAD_SERVICE_APPOINTMENT_FROM_SIGNAL_DEEP_LINK
    ),
    mergeMap((action: fromActions.LoadServiceAppointmentFromSignalDeepLink) => {
      const payload = action instanceof fromActions.LoadServiceAppointmentFromSignalDeepLink ? action.payload : undefined;
      return this.customerService.getApptBySignalDeepLink(payload.signalDeeplinkId).pipe(
        map((data) => new fromActions.LoadServiceAppointmentFromSignalDeepLinkSuccess({ data })),
        catchError(err => of(new fromActions.LoadServiceAppointmentFromSignalDeepLinkFail({ error: err })))
      )
    }
    )
  ));
  constructor(
    private actions$: Actions,
    private availabilityService: AvailabilityService,
    private appointmentService: AppointmentService,
    private vehicleService: VehicleService,
    private catalogService: CatalogService,
    private serviceAppointmentState: Store<ServiceAppointmentState>,
    private readonly customerService: CustomerService
  ) {
  }
}
